import { Theme, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { TranscriptTypes } from '../../constants/enums';
import {
  selectEventAPI,
  selectEventAPIAuth,
  selectFeatureFlagHitl3,
  selectIsHitl7Enabled,
} from '../../redux/features/config/config.selector';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import { selectUserProfile } from '../../selectors/user';
import { UserDetails } from '../../types';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { sendAsrIncorrectEvent } from '../../utils/asrEvents';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import {
  ASR_INCORRECT_ISSUE_TYPE,
  ASR_MISSING_ISSUE_TYPE,
  buildAsrDetailSeed,
} from '../../constants/issueTypes';
import ConfirmDialog from '../common/ConfirmDialog';
import { useDisplayPreference } from '../Layout/DisplayPreferenceContext';
import asrPromotedIcon from '../../images/asr-promoted.svg';

import {
  BlockedReason,
  EntitySpan,
  EntityType,
} from '../../reducers/messagingSlice';

type TranscriptMessageStyleProps = {
  type: TranscriptTypes;
  isBlocked?: boolean;
  blockedReason?: BlockedReason;
  // HITL 3.0 transcript card uses a denser layout (smaller font/spacing); the
  // OLD transcript leaves this false for the original 14px/4px look.
  compact?: boolean;
  // hitl_3_0 flag: drives the shared hitl3 message-bubble font size. Kept
  // separate from `compact` so only the flagged UI is affected, never legacy.
  isHitl3?: boolean;
};

const getBlockedColor = (blockedReason?: BlockedReason): string => {
  if (blockedReason === BlockedReason.debounce) {
    return Colors.blockedDebounce;
  }
  if (blockedReason === BlockedReason.pendingAsr) {
    return Colors.blockedPendingAsr;
  }
  return Colors.blockedDefault;
};

const getBlockedTooltipText = (blockedReason?: BlockedReason): string => {
  if (blockedReason) {
    return `Blocked: ${blockedReason}`;
  }
  return 'Blocked';
};

// Compact (HITL 3.0) bubbles show a WhatsApp-style clock at the bottom-right,
// derived from the message's ISO `timestamp`. 12-hour with am/pm, locale-forced
// so it reads "3:45 PM" regardless of the agent's locale. When hitl_3_0 is on it
// includes seconds ("3:45:07 PM"). Returns '' for a missing/invalid timestamp so
// nothing renders.
const formatClockTime = (timestamp?: string, withSeconds = false): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  if (withSeconds) options.second = '2-digit';
  return date.toLocaleTimeString('en-US', options);
};

const ENTITY_HIGHLIGHT_COLORS: Record<
  EntityType,
  { background: string; color: string }
> = {
  menu_item: { background: '#BBDEFB', color: '#1565C0' },
  quantity: { background: '#C8E6C9', color: '#2E7D32' },
  coupon: { background: '#FFE0B2', color: '#E65100' },
  discount: { background: '#F8BBD0', color: '#AD1457' },
  loyalty: { background: '#E1BEE7', color: '#6A1B9A' },
  size: { background: '#C5CAE9', color: '#283593' },
};

// ── HITL 7.0 transcript line (HITL-1667) ─────────────────────────────────────
// The compact card's chat bubbles give way to plain left-aligned lines. Speaker
// is carried by a short label and by text colour instead of by position and
// fill, which is what the bubbles, their tails and the alternating alignment
// were spending most of a message's height on.
//
// Inline styles rather than new rules in the `useStyles` block below, on
// purpose: this repo's JSS class names come from one GLOBAL counter, so adding
// a rule to any makeStyles block renumbers class names in unrelated suites and
// breaks their snapshots. Flag-off rendering has to stay byte-identical, so the
// 7.0 look is applied without touching that block at all.

// Near-black — the same tone the AI already carries in both existing layouts, so
// only the customer's colour is actually new. 16.9:1 on white.
const HITL7_AI_TEXT_COLOR = '#1E1E1E';
// Rust for the customer, NOT the legacy layout's guardsmanRed (#CC0001). Down a
// whole pane of left-aligned lines a pure saturated red reads as a column of
// errors rather than as the other speaker; rust separates the two voices without
// claiming anything is wrong.
//
// Deliberately not Colors.Thunderbird or Colors.chelseaGem, the two rusts
// already in the palette: those are spoken for as the cart's error colour and
// the order banner's warning colour, and a third meaning on the same swatch is
// how a palette stops meaning anything.
// 6.5:1 on white, comfortably past the 4.5:1 minimum.
const HITL7_CX_TEXT_COLOR = '#9C4221';

// Fixed-width label gutter, sized to the WIDER of the two labels ("CX") with
// about a pixel to spare. The message text is a SEPARATE flex child, which is
// what makes a wrapped line hang under the text instead of running back under
// the label.
//
// Kept deliberately tight. The column has to be fixed — that is what starts
// every message at the same x — but any width beyond the widest label turns
// into a hole between the label and the words, and it is charged to EVERY line
// in the pane. At 20px + 6px that hole read as roughly 14px after "AI", which
// is wider than the space between two words.
const HITL7_LABEL_WIDTH = '18px';
const HITL7_LABEL_GAP = '4px';
// 17px line + 4px beneath = 21px a message, down from the bubble's 35px (the
// same 17px line, plus 12px of vertical padding and a 6px margin). Halving the
// per-message height is the point of the ticket: it is where the extra
// conversation on screen comes from.
const HITL7_LINE_HEIGHT = '17px';
const HITL7_LINE_GAP = '4px';

// The secondary-ASR "promoted" badge, sitting ON the line rather than under it.
//
// The shared compact class aligns it `text-bottom`, which is the font's
// DESCENDER line — about 3px below the baseline — so the badge hung visibly
// below the words it belongs to. `middle` centres it on the text's own optical
// centre (baseline + half the x-height) instead, which is what reads as "same
// line".
//
// The height drops 14 → 12 to pay for that. Centring on the optical centre
// pushes the top of the badge upward, and at 14px it would have poked past the
// 17px line box and grown that one message taller than every other line in the
// pane — the exact ragged spacing this ticket set out to remove. At 12px it
// spans roughly 2.5px below the baseline to 9.5px above it, comfortably inside
// the line box.
const HITL7_PROMOTED_ICON_STYLE: React.CSSProperties = {
  verticalAlign: 'middle',
  height: '12px',
};

const HITL7_LINE_STYLE: React.CSSProperties = {
  display: 'flex',
  // First baselines align, so the smaller label sits ON the text's baseline
  // rather than floating above it.
  alignItems: 'baseline',
  gap: HITL7_LABEL_GAP,
  marginBottom: HITL7_LINE_GAP,
  fontSize: HITL3_FONT_SIZE,
  lineHeight: HITL7_LINE_HEIGHT,
  wordBreak: 'break-word',
};

const HITL7_LABEL_STYLE: React.CSSProperties = {
  flex: `0 0 ${HITL7_LABEL_WIDTH}`,
  fontSize: '0.6875rem',
  fontWeight: 700,
  // Centred, so the slack left over by the narrower label ("AI" is ~5px shorter
  // than "CX") is split either side of it instead of all landing between the
  // label and the text — which is what made the gap after "AI" read as larger
  // than the gap after "CX". Left-aligning is what a fixed column does by
  // default and it is the wrong default here.
  textAlign: 'center',
  // The label is a gutter marker, not speech: copying a stretch of transcript
  // should give the words that were said, not AI/CX down the left-hand side.
  userSelect: 'none',
};

// minWidth 0 so a long unbroken utterance wraps INSIDE this column instead of
// widening the row — without it a flex item refuses to shrink below its content
// and the line would push a horizontal scrollbar into the pane at 1024px.
const HITL7_TEXT_STYLE: React.CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
};

const useStyles = makeStyles<Theme, TranscriptMessageStyleProps>(({}) => ({
  transcriptMessageClass: {
    fontSize: ({ compact }) => (compact ? '12px' : '14px'),
    lineHeight: '20px',
    paddingBottom: ({ compact }) => (compact ? '2px' : '4px'),
    display: 'flex',
    color: ({ type, isBlocked, blockedReason }) =>
      isBlocked
        ? getBlockedColor(blockedReason)
        : type === TranscriptTypes.AI
        ? '#1E1E1E'
        : Colors.guardsmanRed,
    textDecoration: ({ isBlocked }) => (isBlocked ? 'line-through' : 'none'),
    textDecorationThickness: ({ isBlocked }) => (isBlocked ? '3px' : 'auto'),
    fontWeight: ({ isBlocked }) => (isBlocked ? 'bold' : 'normal'),
  },
  sourceClass: {
    // HITL 3.0 (compact): no fixed width — the source label sizes to its text
    // and the content flows naturally. Legacy keeps the original fixed widths
    // (the calc below also accounted for the language indicator, which compact
    // doesn't render).
    width: ({ compact }) => (compact ? undefined : '1.75rem'),
    fontWeight: 'bold',
    textDecoration: 'inherit',
  },
  contentClass: {
    width: ({ compact }) => (compact ? undefined : 'calc(100% - 3.75rem)'),
    textDecoration: 'inherit',
  },
  wordSpanClass: {
    display: 'inline-block',
    padding: '0 2px',
    borderRadius: '3px',
    textDecoration: 'inherit',
    '&:hover': {
      backgroundColor: '#fff59d',
      cursor: 'pointer',
    },
  },
  langIndicatorClass: {
    width: '2rem',
    textAlign: 'center',
    paddingRight: '4px',
  },
  secondLangIndicatorClass: {
    backgroundColor: '#0797EA',
    color: '#FFFFFF',
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  secondLangClass: {
    fontStyle: 'italic',
  },
  entityHighlightClass: {
    borderRadius: '3px',
    padding: '0 2px',
    fontWeight: 600,
  },
  // secondary_asr: "promoted" badge sizing. width:auto keeps the SVG's aspect
  // ratio; the badge sits after the message content (before the timestamp in the
  // compact bubble) on the text's baseline. marginLeft separates it from the
  // preceding text. Slightly smaller in the compact bubble than in legacy.
  promotedIconClass: {
    display: 'inline-block',
    verticalAlign: 'text-bottom',
    height: '16px',
    width: 'auto',
    marginLeft: '4px',
  },
  promotedIconCompactClass: {
    display: 'inline-block',
    verticalAlign: 'text-bottom',
    height: '14px',
    width: 'auto',
    marginLeft: '3px',
  },
  // --- Compact (HITL 3.0 card) only: conversational chat bubbles. AI is
  // right-aligned, the customer (CX) left-aligned, so the two speakers read as
  // a back-and-forth. Legacy (non-compact) transcript is untouched.
  bubbleRow: {
    display: 'flex',
    width: '100%',
    marginBottom: '6px',
  },
  bubbleRowAI: {
    justifyContent: 'flex-end',
  },
  bubbleRowCX: {
    justifyContent: 'flex-start',
  },
  bubble: {
    position: 'relative',
    maxWidth: '100%',
    padding: '6px 10px',
    borderRadius: '12px',
    // hitl_3_0 uses the shared hitl3 size; the compact-but-flag-off bubble keeps
    // its original 12px so only the flagged UI grows.
    fontSize: ({ isHitl3 }) => (isHitl3 ? HITL3_FONT_SIZE : '12px'),
    lineHeight: '17px',
    wordBreak: 'break-word',
  },
  // AI bubble: pale green tint of the "Order Flow" intent-group color
  // (Colors.welcome @ ~12%), with a WhatsApp-style tail at the TOP-RIGHT — the
  // corner is squared and a small triangle (same tint) flares outward from it.
  bubbleAI: {
    backgroundColor: `${Colors.welcome}1F`,
    color: '#1E1E1E',
    borderTopRightRadius: 0,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: -6,
      width: 6,
      height: 8,
      backgroundColor: `${Colors.welcome}1F`,
      clipPath: 'polygon(0 0, 100% 0, 0 100%)',
    },
  },
  // CX bubble: pale blue tint of the "Conversation" intent-group color
  // (Colors.readBack @ ~12%), mirrored tail at the TOP-LEFT.
  bubbleCX: {
    backgroundColor: `${Colors.readBack}1F`,
    color: '#1E1E1E',
    borderTopLeftRadius: 0,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: -6,
      width: 6,
      height: 8,
      backgroundColor: `${Colors.readBack}1F`,
      clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
    },
  },
  // WhatsApp-style timestamp (hitl_3_0 OFF): stacked on its own line at the
  // bottom-right of the bubble.
  bubbleTime: {
    display: 'block',
    textAlign: 'right',
    fontSize: '10px',
    lineHeight: 1,
    marginTop: 3,
    color: 'rgba(0, 0, 0, 0.45)',
    fontVariantNumeric: 'tabular-nums',
  },
  // hitl_3_0 ON: lay the bubble out as a row so the message text and the
  // timestamp share the same line — text on the left, stamp on the right —
  // instead of the stamp dropping to its own line. `last baseline` sits the
  // smaller stamp on the text's baseline (bottom line when the text wraps), so
  // it lines up with the words rather than floating slightly high.
  bubbleFlexHitl3: {
    display: 'flex',
    alignItems: 'last baseline',
    gap: 8,
  },
  // hitl_3_0 ON: the message text is the growing flex child; min-width:0 lets it
  // wrap within the row instead of shoving the timestamp off it.
  bubbleContentFlex: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  // hitl_3_0 ON: the timestamp rides at the right end of the text, in a distinct
  // monospace face + muted tone so it reads as metadata. flexShrink:0 so it keeps
  // its width and the text wraps around it rather than the stamp being squeezed.
  bubbleTimeHitl3: {
    flexShrink: 0,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: '10px',
    lineHeight: '17px',
    letterSpacing: '0.02em',
    color: 'rgba(0, 0, 0, 0.45)',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
}));

interface ITranscriptMessage {
  text: string;
  type: TranscriptTypes;
  secondLangText?: string;
  isBlocked?: boolean;
  blockedReason?: BlockedReason;
  entityAnnotations?: EntitySpan[];
  // secondary_asr: when true, render a "promoted" badge before the text.
  // Already gated on the secondary-ASR flags (see config.flags) by the parent
  // (MessageContainer), so this is presentational only.
  isPromoted?: boolean;
  compact?: boolean;
  // ISO timestamp of the message; rendered as a WhatsApp-style clock in the
  // compact bubble. Ignored by the legacy layout.
  timestamp?: string;
}

export const TranscriptMessage = ({
  text = '•',
  type,
  secondLangText = '',
  isBlocked = false,
  blockedReason,
  entityAnnotations,
  isPromoted = false,
  compact = false,
  timestamp,
}: ITranscriptMessage) => {
  const eventAPI = useAppSelector(selectEventAPI);
  const eventAPIAuth = useAppSelector(selectEventAPIAuth);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  // HITL-1695: under 7.0 the agent picks the plain line (HITL-1667, the
  // default) or the 3.0 bubble. `pref` is already the defaults with 7.0 off.
  const { pref: displayPref } = useDisplayPreference();
  const isHitl7Line = isHitl7 && displayPref.transcriptStyle === 'plain';
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const userDetails = useAppSelector(selectUserProfile) as UserDetails | null;
  const dispatch = useAppDispatch();
  const effectiveSessionId = useAppSelector(
    (state) =>
      state.taskRouter.taskAssignedSessionId ||
      state.sessionBoundary.sessionId ||
      state.sessionBoundary.endedSessionId ||
      state.messages.currentSessionId
  );

  const {
    transcriptMessageClass,
    sourceClass,
    contentClass,
    langIndicatorClass,
    secondLangClass,
    secondLangIndicatorClass,
    wordSpanClass,
    entityHighlightClass,
    bubbleRow,
    bubbleRowAI,
    bubbleRowCX,
    bubble,
    bubbleAI,
    bubbleCX,
    bubbleTime,
    bubbleTimeHitl3,
    bubbleContentFlex,
    bubbleFlexHitl3,
    promotedIconClass,
    promotedIconCompactClass,
  } = useStyles({
    type,
    isBlocked,
    blockedReason,
    compact,
    isHitl3,
  });

  const lastClickRef = useRef<{ word: string; time: number } | null>(null);

  const words = useMemo(() => {
    if (!text) {
      return [] as string[];
    }
    return text.split(/\s+/).filter((w) => w.length > 0);
  }, [text]);

  const annotatedSegments = useMemo(() => {
    if (!entityAnnotations?.length || !text) return null;
    const sorted = [...entityAnnotations].sort((a, b) => a.start - b.start);
    const segments: { text: string; entity?: EntitySpan }[] = [];
    let cursor = 0;
    for (const span of sorted) {
      if (
        span.start < cursor ||
        span.end <= span.start ||
        span.end > text.length
      )
        continue;
      if (span.start > cursor) {
        segments.push({ text: text.slice(cursor, span.start) });
      }
      segments.push({
        text: text.slice(span.start, span.end),
        entity: span,
      });
      cursor = span.end;
    }
    if (cursor < text.length) {
      segments.push({ text: text.slice(cursor) });
    }
    return segments;
  }, [text, entityAnnotations]);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    word: string;
  }>({ open: false, word: '' });

  /**
   * HITL-1501: with issue reporting on, clicking a word files a real issue report
   * classified `Incorrect Transcription`, with the detail field prefilled so the
   * agent only has to type what the customer actually said. The confirm dialog goes
   * with it — the flag panel IS the confirmation and it carries Undo.
   *
   * GATED, not deleted: the flag is per-store, so with it off everything below
   * behaves exactly as it did. This is also the path that keeps the misheard word in
   * the payload — the contract has no `word_clicked` field, so the prefill is where
   * it lives.
   *
   * `asr_incorrect` is emitted on BOTH sides of the gate for the pilot, so the
   * existing series stays continuous while the new one is proved out — see the
   * dual-emit note at the call below.
   */
  const { isEnabled: isIssueReportingEnabled, flagIssue } = useIssueReport();

  const openConfirmDialog = useCallback(
    (wordClickedRaw: string | undefined) => {
      const wordClicked =
        !wordClickedRaw || wordClickedRaw === '/silence'
          ? 'ASR MISSING'
          : wordClickedRaw;

      const now = Date.now();
      if (
        lastClickRef.current &&
        lastClickRef.current.word === wordClicked &&
        now - lastClickRef.current.time < 500
      ) {
        return;
      }
      lastClickRef.current = { word: wordClicked, time: now };

      if (isIssueReportingEnabled) {
        const isMissing = wordClicked === 'ASR MISSING';
        // PILOT DUAL-EMIT. The legacy `asr_incorrect` event keeps going out
        // alongside the flag, so any dashboard or NR query counting it does not read
        // a pilot store as having stopped having ASR problems. The two are
        // independent series — the flag lands as an issue report with its own shape —
        // and dropping this line is a one-word change once the consumers have moved.
        //
        // Sent WITHOUT the confirm dialog the legacy path used, because the flag
        // panel is the confirmation now. That makes the pilot series slightly more
        // sensitive than the old one (no second step to abandon), which is the right
        // direction for a continuity signal.
        sendAsrIncorrectEvent({
          eventAPI,
          eventAPIAuth,
          restaurantCode,
          effectiveSessionId,
          userDetails,
          wordClicked,
          dispatch,
        });
        flagIssue({
          source: 'transcript_turn',
          // A click on `/silence` or an empty word is the same statement as a click
          // on empty transcript, so it files as Missing rather than as an Incorrect
          // report about a word that was never heard.
          issue_type: isMissing
            ? ASR_MISSING_ISSUE_TYPE
            : ASR_INCORRECT_ISSUE_TYPE,
          // Left unfinished this still names the misheard word, which is the whole
          // reason this gesture survives at all. Reads as a half-sentence in the
          // warehouse; that is the accepted cost of not losing `word_clicked`.
          issue_detail: isMissing ? null : buildAsrDetailSeed(wordClicked),
        });
        return;
      }

      setConfirmDialog({ open: true, word: wordClicked });
    },
    [
      isIssueReportingEnabled,
      flagIssue,
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      dispatch,
    ]
  );

  const handleConfirm = useCallback(() => {
    const wordClicked = confirmDialog.word;

    sendAsrIncorrectEvent({
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      wordClicked,
      dispatch,
    });

    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }, [
    confirmDialog.word,
    eventAPI,
    eventAPIAuth,
    restaurantCode,
    effectiveSessionId,
    userDetails,
    dispatch,
  ]);

  const handleCloseDialog = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const handleContentClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      const target = event.target as HTMLElement | null;
      const wordFromTarget = target?.dataset?.word;
      if (!wordFromTarget) {
        return;
      }

      openConfirmDialog(wordFromTarget);
    },
    [openConfirmDialog]
  );

  const contentInner = (
    <>
      {annotatedSegments
        ? annotatedSegments.map((seg, i) => {
            const segWords = seg.text.split(/\s+/).filter((w) => w.length > 0);
            const wordSpans = segWords.map((word, wi) => (
              <span
                key={`${word}-${i}-${wi}`}
                data-word={word}
                className={wordSpanClass}
              >
                {wi > 0 ? ' ' : ''}
                {word}
              </span>
            ));
            if (seg.entity) {
              return (
                <Tooltip
                  key={`entity-${i}`}
                  title={seg.entity.type.replaceAll('_', ' ')}
                  placement="top"
                >
                  <span
                    className={entityHighlightClass}
                    style={{
                      backgroundColor:
                        ENTITY_HIGHLIGHT_COLORS[seg.entity.type]?.background ??
                        '#E0E0E0',
                      color: ENTITY_HIGHLIGHT_COLORS[seg.entity.type]?.color,
                    }}
                  >
                    {wordSpans}
                  </span>
                </Tooltip>
              );
            }
            return (
              <React.Fragment key={`gap-${i}`}>{wordSpans}</React.Fragment>
            );
          })
        : words.length === 0
        ? text
        : words.map((word, index) => (
            <span
              key={`${word}-${index}`}
              data-word={word}
              className={wordSpanClass}
            >
              {index > 0 ? ' ' : ''}
              {word}
            </span>
          ))}
      {secondLangText ? (
        <span className={secondLangClass}>{`     | ${secondLangText}`}</span>
      ) : null}
    </>
  );

  const isAI = type === TranscriptTypes.AI;
  // HITL 3.0 shows seconds (hh:mm:ss); legacy stays hh:mm.
  const timeLabel = formatClockTime(timestamp, isHitl3);

  // secondary_asr: small waveform+arrow badge marking a CX line whose text was
  // promoted (higher-confidence) by ASR. Rendered inline before the text in both
  // the compact bubble and the legacy layout. `isPromoted` is already gated on
  // the secondary-ASR flags by MessageContainer.
  const promotedIcon = isPromoted ? (
    <Tooltip
      title="Promoted by secondary ASR (higher confidence)"
      placement="top"
    >
      <img
        src={asrPromotedIcon}
        alt="ASR promoted"
        className={compact ? promotedIconCompactClass : promotedIconClass}
        // Inline override rather than a change to the class: the class is shared
        // with the 3.0 bubble, where the badge sits in a padded box and the
        // text-bottom alignment is fine.
        // So it follows the LINE, not the flag: a 7.0 agent who chose the
        // bubble gets the bubble's alignment (HITL-1695).
        style={compact && isHitl7Line ? HITL7_PROMOTED_ICON_STYLE : undefined}
      />
    </Tooltip>
  ) : null;

  // Compact card: a chat bubble aligned by speaker. Legacy layout otherwise.
  // The blocked (strike-through) styling sits on the content — not the whole
  // bubble — so the timestamp below it stays legible.
  const bubbleMarkup = (
    <div className={`${bubbleRow} ${isAI ? bubbleRowAI : bubbleRowCX}`}>
      <div
        className={`${bubble} ${isAI ? bubbleAI : bubbleCX} ${
          isHitl3 ? bubbleFlexHitl3 : ''
        }`}
      >
        <div
          className={
            isHitl3 ? `${contentClass} ${bubbleContentFlex}` : contentClass
          }
          onClick={handleContentClick}
          style={
            isBlocked
              ? {
                  textDecoration: 'line-through',
                  textDecorationThickness: '3px',
                  color: getBlockedColor(blockedReason),
                  fontWeight: 'bold',
                }
              : undefined
          }
        >
          {contentInner}
        </div>
        {promotedIcon}
        {timeLabel ? (
          <span className={isHitl3 ? bubbleTimeHitl3 : bubbleTime}>
            {timeLabel}
          </span>
        ) : null}
      </div>
    </div>
  );

  const messageContent = (
    <div className={transcriptMessageClass}>
      {!compact && (
        <div className={langIndicatorClass}>
          <div className={secondLangIndicatorClass}>
            {secondLangText ? 'S' : ''}
          </div>
        </div>
      )}
      <div className={sourceClass}>{type}:&nbsp;</div>
      <div className={contentClass} onClick={handleContentClick}>
        {contentInner}
      </div>
      {promotedIcon}
    </div>
  );

  // HITL-1667 (hitl_7_0): a plain left-aligned line — AI/CX label in a fixed
  // gutter, then the utterance, and nothing else. No bubble, no tail, and no
  // timestamp: the agent is watching a live conversation, not auditing a log,
  // and the clock was costing width on every single message to say something
  // they can already see happening.
  //
  // Entity highlighting is untouched by design. Each highlight sets its OWN
  // background and foreground inline, so it overrides the speaker colour it sits
  // inside and renders identically against near-black and against rust.
  const lineMarkup = (
    <div
      data-testid="transcript-line"
      style={{
        ...HITL7_LINE_STYLE,
        color: isAI ? HITL7_AI_TEXT_COLOR : HITL7_CX_TEXT_COLOR,
      }}
    >
      <span style={HITL7_LABEL_STYLE}>{type}</span>
      <div
        className={contentClass}
        onClick={handleContentClick}
        style={
          isBlocked
            ? {
                ...HITL7_TEXT_STYLE,
                textDecoration: 'line-through',
                textDecorationThickness: '3px',
                color: getBlockedColor(blockedReason),
                fontWeight: 'bold',
              }
            : HITL7_TEXT_STYLE
        }
      >
        {contentInner}
        {promotedIcon}
      </div>
    </div>
  );

  // `compact` is the 3.0 transcript CARD; the legacy transcript never reaches
  // either of the two branches above it.
  const rendered = compact
    ? isHitl7Line
      ? lineMarkup
      : bubbleMarkup
    : messageContent;

  return (
    <>
      {isBlocked ? (
        <Tooltip title={getBlockedTooltipText(blockedReason)} placement="top">
          {rendered}
        </Tooltip>
      ) : (
        rendered
      )}
      <ConfirmDialog
        // Portal-rendered, so it needs the transcript marker itself — confirming
        // a mis-heard word is a report, not a take-over of the order.
        data-transcript="true"
        open={confirmDialog.open}
        content={`Send ASR incorrect event for word: "${
          confirmDialog.word || 'ASR MISSING'
        }"?`}
        onClose={handleCloseDialog}
        onConfirm={handleConfirm}
        closeBtnText="Cancel"
        confirmBtnText="OK"
      />
    </>
  );
};
